import type { StoryType } from "@config";
import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  useGenerateOutlineMutation,
  useGenerateSlideMutation,
  useGenerateSlidesLayoutsMutation,
} from "@/shared/services/docpilot";
import { buildTemplateStoryFromSlides } from "@/shared/utils/import-story-from-json";
import type { Slide } from "../components/sortable-slide";
import { useBrandPrefillStore } from "../store/brand-prefill.store";
import { createRosterLookup } from "../utils/team-roster";
import type { RosterLookup } from "../utils/team-roster";
import { useRunStoryAgentMutation } from "@api/ai";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/feature-flags";
import type {
  GenerateOutlineResult,
  OutlineItem,
  SlideLayoutMapping,
} from "../types/generation";
import { buildOutlineRequest } from "../utils/build-generation-payload";
import { FunnelUserError, funnelErrorMessage } from "../utils/funnel-errors";
import { normalizeSlideThemeVars } from "../utils/normalize-slide-theme-vars";
import { readTemplateStory } from "../utils/template-source";
import {
  type TemplateDesignSource,
  withTemplateDesign,
} from "../utils/template-design";
import {
  pickedSlideMapping,
  type TemplateSlideMapping,
} from "../utils/template-slide-mappings";
import {
  buildDeckOutline,
  pairMappingsWithOutline,
} from "../utils/deck-outline";
import {
  SLIDE_GENERATION_CONCURRENCY,
  SLIDE_RETRY_BASE_DELAY_MS,
  delay,
  runWithConcurrency,
} from "../utils/run-with-concurrency";

export type OutlineInputs = Parameters<typeof buildOutlineRequest>[0];

/** One slide of the plan: what to say, in which design, filled or copied. */
export type DeckPlanPair = {
  mapping: SlideLayoutMapping;
  slide: OutlineItem;
  /** The template's own slide this one fills, when it fills one. */
  templateSlide?: unknown;
  /**
   * Copy the template slide in untouched instead of filling it. The user
   * locked it on the prompt step; see ../utils/current-outline.
   */
  locked?: boolean;
};

/**
 * A deck decided but not yet written: the outline, the design mapping for
 * each slide, and the template's own slides where there is a template.
 *
 * Plain data on purpose. It is handed across a navigation -- from the funnel
 * that planned it to the runner that finishes it in the editor -- so it holds
 * nothing that a React tree owns.
 */
export type DeckPlan = {
  outline: GenerateOutlineResult;
  pairs: DeckPlanPair[];
  templateSlides: unknown[] | null;
  /**
   * The template's own story, for its theme. Without it a deck assembled
   * from DEFAULT_STORY carries the default theme, and "Use template's theme"
   * -- which applies nothing on top -- would keep it. See ../utils/template-design.
   */
  templateStory: TemplateDesignSource;
  storyTitle: string;
  /** Leave the template's pictures alone; see DeckInputs. */
  keepImages: boolean;
  /**
   * Who sends the deck, as the prompt step resolved it. A template's
   * copyright line names this company rather than the template's own; see
   * ../utils/rebrand-copyright and the backend's fill.
   */
  companyName: string | null;
  /**
   * The deck's verified people, for any slide that could name one.
   *
   * Resolves to null when nothing could be confirmed, which is what makes the
   * generator write "Add name here" instead of someone it half-remembers.
   * See ../utils/team-roster.
   */
  roster: RosterLookup;
};

export interface DeckInputs {
  /** The approved outline, including any user reorders/edits from outline-result. */
  outline: Slide[];
  /** Full outline result from /generate-outline — tone, image guidelines, title. */
  outlineMeta: GenerateOutlineResult | null;
  docType: string;
  templateId: string | null;
  /**
   * The picked template's own designs, one per slide, in order.
   *
   * When present the model is not asked to choose layouts at all: the user
   * chose them by choosing the template. Null on every non-template path, and
   * on a template that cannot answer for all of its slides — see
   * ../utils/template-slide-mappings.
   */
  templateSlideMappings?: TemplateSlideMapping[] | null;
  /**
   * Where the picked template's story lives.
   *
   * Its slides are sent to the generator to be filled directly, which is the
   * only way the deck comes out in the template's own designs: the backend
   * resolves a library design BY NAME first, so a design id never gets a look
   * in while the family name matches something.
   */
  templateContentPointer?: string | null;
  /**
   * The org story the template is, or was cut from, when it is one. Read
   * only when the deck becomes a version: it hangs off that story.
   */
  basePageId?: string | null;
  /**
   * The template's story in memory, when it was assembled rather than stored
   * -- slides picked in the library. Read in place of the pointer.
   */
  templateStory?: Record<string, unknown> | null;
  /**
   * Keep every picture of the template's slides as it is.
   *
   * Offered when the template is one of the user's own documents, whose
   * pictures may be theirs to keep -- a product shot, a PNG icon -- rather
   * than stand-ins to rewrite. The backend keeps recognisable uploads and
   * icons in any case; this turns the rewrite off for the rest too.
   */
  keepImages?: boolean;
  fileUrl: string | null;
  companyName: string | null;
  /**
   * The domain of `companyName`, from the same source (usePromptCompany).
   * The research below tells one company of a name from another by it, so
   * it must never be a different company's -- the email's -- domain.
   */
  companyDomain?: string | null;
}

// Empty on purpose, exactly as the production wizard sent it: the deployed
// backend picks its own supported image provider/model. Hardcoding a model
// here pins every environment to it, whether or not that deployment
// supports it. The field itself must exist — the route dereferences it.
const DEFAULT_AI_IMAGE = {} as const;

/**
 * The funnel's brand in the shape the team research reads a StoryBrain in.
 *
 * Only the company identity is needed, and the domain is the part that
 * matters: it is what separates one "Apex" from the dozen other companies of
 * that name. Null when the funnel never resolved a brand.
 *
 * The company the outline was written for, when the prompt step resolved one
 * -- its name and its domain together. The prefill only stands in when there
 * is none: pairing the Brain's company name with the email's domain sent the
 * research looking for "Kiezbrot" at storydoc.com.
 */
const brandAsBrain = (i: Pick<DeckInputs, "companyName" | "companyDomain">) => {
  const brand = useBrandPrefillStore.getState().brand;
  if (i.companyName && i.companyName !== brand?.name) {
    return {
      brand: {
        name: i.companyName,
        domain: i.companyDomain ?? "",
        description: "",
      },
    };
  }
  if (!brand?.name && !brand?.domain) return null;
  return {
    brand: {
      name: brand?.name ?? "",
      domain: brand?.domain ?? "",
      description: brand?.description ?? "",
    },
  };
};

export const useFunnelGeneration = () => {
  const [runStoryAgent] = useRunStoryAgentMutation();
  // Team research is the story agent's pipeline (a billed web search), so it
  // ships with the agent: off, slides write placeholders as they always did.
  const isAiAgentEnabled = useFeatureFlag(FEATURE_FLAGS.AI_AGENT);
  const { mutateAsync: generateOutlineMutation } = useGenerateOutlineMutation();
  const { mutateAsync: generateSlidesLayoutsMutation } =
    useGenerateSlidesLayoutsMutation();
  const { mutateAsync: generateSlideMutation } = useGenerateSlideMutation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks concurrently in-flight operations so isProcessing only clears
  // once every call started on this hook instance has settled.
  const pendingCount = useRef(0);

  const beginOperation = useCallback(() => {
    pendingCount.current += 1;
    setIsProcessing(true);
    setError(null);
  }, []);

  const endOperation = useCallback(() => {
    pendingCount.current = Math.max(0, pendingCount.current - 1);
    setIsProcessing(pendingCount.current > 0);
  }, []);

  const generateOutline = useCallback(
    async (
      i: OutlineInputs,
      onPartialSlides?: (slides: OutlineItem[]) => void,
      signal?: AbortSignal,
    ): Promise<GenerateOutlineResult> => {
      beginOperation();
      try {
        const result = await generateOutlineMutation({
          body: buildOutlineRequest(i),
          onPartialSlides,
          signal,
        });
        return result;
      } catch (e) {
        setError(funnelErrorMessage(e, "generate the outline"));
        throw e;
      } finally {
        endOperation();
      }
    },
    [generateOutlineMutation, beginOperation, endOperation],
  );

  /**
   * PLANNING A DECK, which is everything that must be decided before a single
   * slide can be asked for: the outline in its request shape, the template's
   * own slides, and one design mapping per slide.
   *
   * Split out of generateDeck because a deck is no longer built in one place.
   * The funnel now plans the deck and generates only its FIRST slide -- just
   * enough to create the story and open the editor on real content -- and
   * ../deck-build-runner generates the rest into that already-open editor, so
   * the user watches the deck fill instead of watching a loader. Both work
   * from this one plan, which is what keeps the slides that arrive the slides
   * the outline asked for, in the order it asked for them.
   */
  const planDeck = useCallback(
    async (i: DeckInputs, signal?: AbortSignal): Promise<DeckPlan> => {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      // Who sends the deck: the outline, the research and the copyright line
      // all name this one company.
      const companyName =
        i.companyName ?? useBrandPrefillStore.getState().brand?.name ?? null;
      const outline = buildDeckOutline({
        slides: i.outline,
        outlineMeta: i.outlineMeta,
        docType: i.docType,
        companyName,
      });

      // THE TEMPLATE ALREADY ANSWERED THIS.
      //
      // Choosing a layout per slide is the right question when there is no
      // template -- the content decides the design. On a template path the
      // user chose the designs by choosing the template, and a model picking
      // its own gives a different deck that merely has the same number of
      // slides. So the template's slides answer instead, each row filling the
      // template slide it stands for. It also saves the call, and the retry
      // below it.
      //
      // THE TEMPLATE'S OWN SLIDES, fetched once for the whole deck.
      //
      // Each is sent with its outline item and filled directly, which is
      // what makes the result the template rather than a deck of library
      // designs that happens to have the same shape. Fails soft: without
      // them the pairing below still asks for the right kinds.
      let templateSlides: unknown[] | null = null;
      let templateStory: TemplateDesignSource = null;
      if (i.templateStory || i.templateContentPointer) {
        try {
          const story = await readTemplateStory(i);
          const slides = (story as { slides?: unknown } | null)?.slides;
          if (Array.isArray(slides) && slides.length > 0) {
            templateSlides = slides;
            templateStory = story as TemplateDesignSource;
          }
        } catch (error) {
          console.error("Failed to read the template's slides:", error);
        }
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      }

      // WHICH TEMPLATE SLIDE EACH ROW FILLS. The prompt step lists the
      // template's slides and lets the user reorder, delete, lock and add to
      // them, so a row remembers the template slide it stands for
      // (templateIndex) rather than being matched to one by position. A row
      // with none is the user's own -- added by them, or on a non-template
      // path -- and the template has nothing to say about it: it is laid out
      // the ordinary way. An older draft that carries no indices at all
      // still pairs by position, which is what it meant.
      //
      // ONLY WITH THE SLIDES THEMSELVES. A mapping may stand in a design id
      // for a slide that has none (see ../utils/template-slide-mappings); that
      // is fine while the slide's own tree goes with it, and wrong without --
      // the backend would look the stand-in up and fail. If the template could
      // not be read, the model chooses layouts as it always did.
      const fromTemplate = templateSlides
        ? (i.templateSlideMappings ?? null)
        : null;
      const hasIndices = i.outline.some((s) => s.templateIndex !== undefined);
      const templateIndexOf = (n: number): number | null => {
        const row = i.outline[n];
        if (!row) return null;
        if (row.templateIndex !== undefined) return row.templateIndex;
        return hasIndices ? null : n;
      };
      // A ROW MAY BRING ITS OWN SLIDE, picked from the library on the prompt
      // step. It is filled exactly as a template's slide is -- its own tree
      // goes with the request -- so it needs no template and no layout model.
      const templateSlideAt = (n: number): unknown => {
        const own = i.outline[n]?.slide;
        if (own) return own;
        const at = templateIndexOf(n);
        return at === null ? undefined : templateSlides?.[at];
      };
      const templateMappingAt = (n: number): TemplateSlideMapping | null => {
        const own = i.outline[n]?.slide;
        if (own) return pickedSlideMapping(own);
        const at = templateIndexOf(n);
        return at === null ? null : (fromTemplate?.[at] ?? null);
      };
      const beyondTemplate = outline.slides.filter(
        (_slide, n) => !templateMappingAt(n),
      ).length;

      // One retry, as the wizard does: re-asking the model is the only way
      // to repair a bad mapping set, and it's one call rather than a doomed
      // request per slide.
      let layouts =
        beyondTemplate === 0
          ? null
          : await generateSlidesLayoutsMutation({
              body: { outline },
              signal,
            });
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (beyondTemplate > 0 && !layouts?.slideMappings?.length) {
        await delay(SLIDE_RETRY_BASE_DELAY_MS, signal);
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        layouts = await generateSlidesLayoutsMutation({
          body: { outline },
          signal,
        });
      }

      const modelPairs = pairMappingsWithOutline(
        layouts?.slideMappings ?? [],
        outline,
      );
      const modelByName = new Map(
        modelPairs.map((pair) => [pair.slide.internalName, pair.mapping]),
      );
      const pairs: DeckPlanPair[] = outline.slides.flatMap((slide, n) => {
        const own = templateMappingAt(n);
        const mapping = own
          ? { internalName: slide.internalName, ...own }
          : modelByName.get(slide.internalName);
        if (!mapping) return [];
        const templateSlide = templateSlideAt(n);
        // A LOCKED ROW IS COPIED, NOT FILLED. Only where there is a template
        // slide to copy: a lock on a row of the user's own means nothing.
        const locked = Boolean(i.outline[n]?.locked && templateSlide);
        return [{ mapping, slide, templateSlide, locked }];
      });
      if (pairs.length === 0) {
        throw new FunnelUserError(
          "The AI couldn't lay out any of your slides. This is usually temporary — please try again.",
        );
      }
      if (pairs.length < outline.slides.length) {
        const skipped = outline.slides.length - pairs.length;
        toast.warning(
          `${skipped} outline slide${skipped === 1 ? "" : "s"} couldn't be prepared and ${skipped === 1 ? "was" : "were"} skipped — the rest of your document is unaffected`,
        );
      }

      // REAL PEOPLE, RESEARCHED FOR THE WHOLE DECK.
      //
      // Started here and NOT awaited: the request runs while the slides are
      // being generated, and each slide that could name someone waits for it
      // then. Awaiting here would put a web search in front of the first
      // slide on every deck, including the many that never name anybody.
      const roster: RosterLookup = isAiAgentEnabled
        ? createRosterLookup(
            (args) => {
              // Cancelling the deck cancels its research too.
              if (signal?.aborted) {
                return Promise.reject(
                  new DOMException("Aborted", "AbortError"),
                );
              }
              const request = runStoryAgent({
                promptId: args.promptId,
                variables: args.variables,
              });
              const abort = () => request.abort();
              signal?.addEventListener("abort", abort, { once: true });
              return request
                .unwrap()
                .finally(() => signal?.removeEventListener("abort", abort));
            },
            // The brand as a StoryBrain-shaped context: its DOMAIN is what
            // tells one "Apex" from the dozen others, so the research is not
            // left disambiguating on a bare name.
            brandAsBrain(i),
            companyName,
          )
        : async () => null;

      return {
        outline,
        pairs,
        templateSlides,
        templateStory,
        storyTitle: layouts?.storyTitle || outline.storyTitle || i.docType,
        keepImages: Boolean(i.keepImages),
        companyName,
        roster,
      };
    },
    [generateSlidesLayoutsMutation, runStoryAgent, isAiAgentEnabled],
  );

  /**
   * ONE SLIDE of a planned deck, by position.
   *
   * No retry and no error handling of its own: both belong to whoever is
   * running the batch (runWithConcurrency for a whole deck, the runner's own
   * loop for the streamed remainder), and doing it here as well would square
   * the number of attempts.
   */
  const generateSlideAt = useCallback(
    async (
      plan: DeckPlan,
      index: number,
      signal?: AbortSignal,
    ): Promise<unknown> => {
      const { mapping, slide, templateSlide, locked } = plan.pairs[index];
      // THE USER LOCKED THIS ONE. It goes in as the template has it -- the
      // same JSON, nothing rewritten, no model asked. A copy, so nothing the
      // editor does to the placed slide reaches back into the plan.
      if (locked && templateSlide) return structuredClone(templateSlide);
      // Waits for the deck's roster only when this slide could name a person.
      const realTeam = await plan.roster(slide);
      const { slideJson } = await generateSlideMutation({
        body: {
          slideName: mapping.slideName,
          slideDesignId: mapping.slideDesignId,
          // The template slide this row fills, sent to be filled directly.
          ...(templateSlide ? { slideTemplate: templateSlide } : {}),
          outline: { ...plan.outline, slide },
          ...(plan.keepImages ? { keepImages: true } : {}),
          // The sender, for the slide's copyright line.
          ...(plan.companyName ? { companyName: plan.companyName } : {}),
          // Present only where the people were verified; see ../utils/team-roster.
          ...(realTeam ? { realTeam } : {}),
          aiImage: DEFAULT_AI_IMAGE,
        },
        signal,
      });
      return normalizeSlideThemeVars(slideJson);
    },
    [generateSlideMutation],
  );

  // Generates a whole deck in one call: plan, then every slide, then assemble.
  //
  // This is the signup flow's path, where the finished deck is reviewed on the
  // Ready step before any story exists. The platform flow does NOT come
  // through here any more -- it plans, generates slide one, creates the story
  // and hands the rest to ../deck-build-runner, so that the user waits for one
  // slide rather than for all of them.
  const generateDeck = useCallback(
    async (
      i: DeckInputs,
      onProgress?: (done: number, total: number) => void,
      signal?: AbortSignal,
      onSlide?: (index: number, slideJson: unknown) => void,
    ): Promise<StoryType> => {
      beginOperation();
      // Internal controller aborts sibling slides on mid-batch failure; also
      // linked to the caller's signal (Generating unmount).
      const controller = new AbortController();
      const onExternalAbort = () => controller.abort();
      signal?.addEventListener("abort", onExternalAbort);
      if (signal?.aborted) controller.abort();

      try {
        const plan = await planDeck(i, controller.signal);

        const slideJsons = await runWithConcurrency(
          plan.pairs.length,
          SLIDE_GENERATION_CONCURRENCY,
          (index) => generateSlideAt(plan, index, controller.signal),
          onProgress,
          controller.signal,
          onSlide ? (index, slideJson) => onSlide(index, slideJson) : undefined,
        );

        if (controller.signal.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }

        // Slides that failed every attempt leave gaps — drop them and keep
        // the deck rather than losing everything to one bad slide.
        const usableSlides = slideJsons.filter(
          (slideJson) => slideJson !== undefined,
        );
        if (usableSlides.length === 0) {
          throw new FunnelUserError(
            "None of the slides could be generated. This is usually temporary — please try again.",
          );
        }
        const failedCount = slideJsons.length - usableSlides.length;
        if (failedCount > 0) {
          toast.warning(
            `${failedCount} slide${failedCount === 1 ? "" : "s"} couldn't be generated and ${failedCount === 1 ? "was" : "were"} skipped — the rest of your document is unaffected`,
          );
        }

        return withTemplateDesign(
          buildTemplateStoryFromSlides({
            slides: usableSlides,
            title: plan.storyTitle,
          }),
          plan.templateStory,
        ) as unknown as StoryType;
      } catch (e) {
        controller.abort();
        // Caller cancellation — don't surface as a generation failure.
        if (
          (e instanceof Error && e.name === "AbortError") ||
          signal?.aborted
        ) {
          throw e instanceof Error
            ? e
            : new DOMException("Aborted", "AbortError");
        }
        setError(funnelErrorMessage(e, "generate your document"));
        throw e;
      } finally {
        signal?.removeEventListener("abort", onExternalAbort);
        endOperation();
      }
    },
    [planDeck, generateSlideAt, beginOperation, endOperation],
  );

  return {
    generateOutline,
    planDeck,
    generateSlideAt,
    generateDeck,
    isProcessing,
    error,
  };
};
